import { createAsyncThunk } from '@reduxjs/toolkit';
import { DistributionMode } from 'api/dto/LifecycleSurveys.dto';
import { ProjectModel } from 'api/dto/Project.dto';
import { projectApi } from 'container/projectApi';
import { pulseTemplateLifecycleSurveysRepository } from 'container/pulseTemplateLifecycleSurveysRepository';
import { renameLifecycleProject } from 'reduxStore/project/slice';
import { openSnackbar } from 'reduxStore/snackbars/asyncActions';
import { serializeError } from 'shared/utils/redux';

import { STORE_NAME } from './initialState';
import {
  selectDistributionMode,
  selectOnboardSurvey,
  selectOnboardSurveyName,
  selectOnboardSurveys,
} from './selectors';
import { setDistributionMode, setOnboardSurveyFlags, setOnboardSurveyName } from './slice';

const AUTOMATIC_MODE_DEFAULT_SEND_AT_WEEK = 1;
const FIRST_SEQUENCE_NUMBER = 1;

export const fetchOnboardSurveys = createAsyncThunk(
  `${STORE_NAME}/fetchOnboardSurveys`,
  ({ pulseTemplateId }: { pulseTemplateId: ProjectModel['id'] }) =>
    pulseTemplateLifecycleSurveysRepository.get(pulseTemplateId),
  { serializeError },
);

export const renameOnboardSurvey = createAsyncThunk(
  `${STORE_NAME}/renameOnboardSurvey`,
  async (
    { projectId, name }: { projectId: number; name: string },
    { dispatch, getState, rejectWithValue },
  ) => {
    const previousName = selectOnboardSurveyName(projectId)(getState());
    dispatch(setOnboardSurveyName({ projectId, name }));
    dispatch(renameLifecycleProject({ projectId, name }));

    try {
      await projectApi.rename(projectId, name);
    } catch (error) {
      if (previousName !== undefined) {
        dispatch(setOnboardSurveyName({ projectId, name: previousName }));
        dispatch(renameLifecycleProject({ projectId, name: previousName }));
      }
      dispatch(openSnackbar({ message: 'Could not rename survey. Please try again.' }));
      return rejectWithValue(error);
    }

    return undefined;
  },
  { serializeError },
);

type UpdateOnboardSurveySettingsArg = {
  pulseTemplateId: ProjectModel['id'];
  projectId: number;
  sendAtWeek?: number | null;
  isActive?: boolean;
  showOnReportDashboard?: boolean;
};

export const updateOnboardSurveySettings = createAsyncThunk(
  `${STORE_NAME}/updateOnboardSurveySettings`,
  async (arg: UpdateOnboardSurveySettingsArg, { dispatch, getState, rejectWithValue }) => {
    const { pulseTemplateId, projectId, isActive, showOnReportDashboard } = arg;
    const previous = selectOnboardSurvey(projectId)(getState());
    if (!previous) {
      return rejectWithValue(new Error(`Onboard survey ${projectId} not found in state`));
    }

    const nextSendAtWeek = arg.sendAtWeek !== undefined ? arg.sendAtWeek : previous.sendAtWeek;
    const nextIsActive = isActive ?? previous.isActive;
    // When the parent survey is re-enabled, the show-on-dashboard flag resets to active.
    const nextShowOnReportDashboard =
      showOnReportDashboard ?? (isActive === true ? true : previous.showOnReportDashboard);

    dispatch(
      setOnboardSurveyFlags({
        projectId,
        sendAtWeek: nextSendAtWeek,
        isActive: nextIsActive,
        showOnReportDashboard: nextShowOnReportDashboard,
      }),
    );

    try {
      await pulseTemplateLifecycleSurveysRepository.updateSettings(pulseTemplateId, projectId, {
        sendAtWeek: nextSendAtWeek,
        isActive: nextIsActive,
        showOnReportDashboard: nextShowOnReportDashboard,
      });
    } catch (error) {
      dispatch(
        setOnboardSurveyFlags({
          projectId,
          sendAtWeek: previous.sendAtWeek,
          isActive: previous.isActive,
          showOnReportDashboard: previous.showOnReportDashboard,
        }),
      );
      dispatch(openSnackbar({ message: 'Could not update survey. Please try again.' }));
      return rejectWithValue(error);
    }

    const successMessage = resolveSuccessMessage(arg);
    if (successMessage) {
      dispatch(openSnackbar({ message: successMessage }));
    }

    return undefined;
  },
  { serializeError },
);

type UpdateDistributionModeArg = {
  pulseTemplateId: ProjectModel['id'];
  distributionMode: DistributionMode;
};

export const updateDistributionMode = createAsyncThunk(
  `${STORE_NAME}/updateDistributionMode`,
  async (arg: UpdateDistributionModeArg, { dispatch, getState, rejectWithValue }) => {
    const { pulseTemplateId, distributionMode } = arg;
    const previous = selectDistributionMode(getState());
    if (previous === undefined || previous === distributionMode) {
      return undefined;
    }

    dispatch(setDistributionMode(distributionMode));

    try {
      await pulseTemplateLifecycleSurveysRepository.updateDistributionMode(
        pulseTemplateId,
        distributionMode,
      );
    } catch (error) {
      dispatch(setDistributionMode(previous));
      dispatch(openSnackbar({ message: 'Could not update distribution mode. Please try again.' }));
      return rejectWithValue(error);
    }

    if (distributionMode === DistributionMode.Automatic) {
      const onboardOne = selectOnboardSurveys(getState())?.find(
        (survey) => survey.sequenceNumber === FIRST_SEQUENCE_NUMBER,
      );
      if (onboardOne && onboardOne.sendAtWeek === null) {
        dispatch(
          setOnboardSurveyFlags({
            projectId: onboardOne.projectId,
            sendAtWeek: AUTOMATIC_MODE_DEFAULT_SEND_AT_WEEK,
          }),
        );
        try {
          await pulseTemplateLifecycleSurveysRepository.updateSettings(
            pulseTemplateId,
            onboardOne.projectId,
            {
              sendAtWeek: AUTOMATIC_MODE_DEFAULT_SEND_AT_WEEK,
              isActive: onboardOne.isActive,
              showOnReportDashboard: onboardOne.showOnReportDashboard,
            },
          );
        } catch {
          dispatch(
            setOnboardSurveyFlags({
              projectId: onboardOne.projectId,
              sendAtWeek: onboardOne.sendAtWeek,
            }),
          );
        }
      }
    }

    if (distributionMode === DistributionMode.Manual) {
      const onboardOne = selectOnboardSurveys(getState())?.find(
        (survey) => survey.sequenceNumber === FIRST_SEQUENCE_NUMBER,
      );
      if (onboardOne && onboardOne.sendAtWeek !== null) {
        dispatch(setOnboardSurveyFlags({ projectId: onboardOne.projectId, sendAtWeek: null }));
      }
    }

    dispatch(
      openSnackbar({
        message:
          distributionMode === DistributionMode.Automatic
            ? 'Distribution mode set to Automatic.'
            : 'Distribution mode set to Manual.',
      }),
    );
    return undefined;
  },
  { serializeError },
);

const resolveSuccessMessage = (arg: UpdateOnboardSurveySettingsArg): string | null => {
  if (arg.sendAtWeek !== undefined) {
    return 'Schedule updated';
  }
  if (arg.isActive !== undefined) {
    return arg.isActive ? 'Survey enabled' : 'Survey disabled';
  }
  if (arg.showOnReportDashboard !== undefined) {
    return arg.showOnReportDashboard
      ? 'Survey is now visible on the report dashboard'
      : 'Survey is now hidden from the report dashboard';
  }
  return null;
};
